import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme, alpha } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { getClientSeats, ClientSeatInfo, ClientSeatsResponse } from '../../api/licenseAPI';

export interface ClientSeatsListProps {
  /** Number of clients to display initially (default: 5) */
  initialDisplayCount?: number;
  /** Minimum usage percentage to show the list (default: 80) */
  showThreshold?: number;
  /** Current usage percentage (if known from parent) */
  usagePercentage?: number;
  /** Whether to always show regardless of threshold */
  alwaysShow?: boolean;
  /** Callback when View All is clicked */
  onViewAll?: () => void;
}

/**
 * ClientSeatsList Component (HA-157)
 * Displays a list of clients using license seats when at or near capacity.
 * Helps practitioners decide who to archive vs. upgrade.
 */
const ClientSeatsList: React.FC<ClientSeatsListProps> = ({
  initialDisplayCount = 5,
  showThreshold = 80,
  usagePercentage,
  alwaysShow = false,
  onViewAll,
}) => {
  const theme = useTheme();
  const [data, setData] = useState<ClientSeatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getClientSeats();

      setData(response);
    } catch (err) {
      setError('Failed to load client list');
      console.error('Error fetching client seats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Determine if we should show based on threshold
  const effectivePercentage = usagePercentage ?? data?.usagePercentage ?? 0;
  const shouldShow = alwaysShow || effectivePercentage >= showThreshold;

  if (!shouldShow && !loading) {
    return null;
  }

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Skeleton variant="text" width={150} height={24} />
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // Error state
  if (error || !data) {
    return null; // Fail silently - don't block the UI
  }

  const { clients, inactiveClientCount, clientsUsed, isAtCapacity } = data;
  const displayedClients = expanded ? clients : clients.slice(0, initialDisplayCount);
  const hasMore = clients.length > initialDisplayCount;

  // Format activity text
  const getActivityText = (client: ClientSeatInfo): string => {
    if (!client.lastActivityDate) {
      return 'No activity recorded';
    }
    if (client.daysSinceActivity === 0) {
      return 'Active today';
    }
    if (client.daysSinceActivity === 1) {
      return 'Active yesterday';
    }
    if (client.daysSinceActivity && client.daysSinceActivity <= 7) {
      return `Active ${client.daysSinceActivity} days ago`;
    }
    if (client.daysSinceActivity && client.daysSinceActivity <= 30) {
      return `Active ${Math.floor(client.daysSinceActivity / 7)} weeks ago`;
    }

    return 'Inactive 30+ days';
  };

  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: alpha(theme.palette.background.default, 0.5),
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Clients Using Seats ({clientsUsed})
        </Typography>
        <Button
          component={Link}
          to="/client-management"
          size="small"
          variant="text"
          onClick={handleViewAll}
          sx={{ fontSize: '0.75rem' }}
        >
          View All
        </Button>
      </Box>
      {/* Inactive warning */}
      {inactiveClientCount > 0 && isAtCapacity && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 1,
            p: 1,
            borderRadius: 0.5,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
          }}
        >
          <WarningAmberIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
          <Typography
            variant="caption"
            sx={{
              color: 'warning.main',
            }}
          >
            {inactiveClientCount} inactive client{inactiveClientCount > 1 ? 's' : ''} could be
            archived to free up space
          </Typography>
        </Box>
      )}
      {/* Client list - HA-233: Add max-height with scroll for expanded list */}
      <List
        dense
        disablePadding
        sx={{
          maxHeight: expanded ? 300 : 'none',
          overflow: expanded ? 'auto' : 'visible',
        }}
      >
        {displayedClients.map((client) => (
          <ListItem
            key={client.id}
            disablePadding
            sx={{
              py: 0.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.5),
                borderRadius: 0.5,
              },
            }}
            secondaryAction={
              !client.isActive ? (
                <Chip
                  label="Inactive"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              ) : null
            }
          >
            <ListItemAvatar sx={{ minWidth: 40 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.75rem',
                  backgroundColor: client.isActive
                    ? theme.palette.primary.main
                    : theme.palette.grey[400],
                }}
              >
                {client.firstName?.[0] || <PersonIcon fontSize="small" />}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                  {client.fullName || `${client.firstName} ${client.lastName}`.trim()}
                </Typography>
              }
              secondary={
                <Typography
                  variant="caption"
                  color={client.isActive ? 'text.secondary' : 'warning.main'}
                  noWrap
                >
                  {getActivityText(client)}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
      {/* Expand/Collapse button */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: 'text.secondary' }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography
            variant="caption"
            onClick={() => setExpanded(!expanded)}
            sx={{
              color: 'text.secondary',
              alignSelf: 'center',
              cursor: 'pointer',
            }}
          >
            {expanded ? 'Show less' : `Show ${clients.length - initialDisplayCount} more`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClientSeatsList;
